// Playing back a game somebody sent us, on rules that have moved since.
//
// **The whole problem is that the log and the build disagree.** The log is the
// save format, the replay and the network protocol — but it only means anything
// against the rules that produced it, and this deploys several times a day. A
// report filed on Tuesday and opened on Thursday is a sequence of commands some
// of which the game no longer accepts: a weight became a rule, a guard got
// stricter, a command grew a field. `GameStore.load` replays a log with no
// error handling at all, which is right for a log this build wrote and useless
// for one it did not.
//
// So this replays *correctively*. A command the reducer refuses is skipped, the
// skip is recorded with its index and its reason, and the replay carries on
// from the last good state. What comes out is a playable game plus an honest
// account of what could not be played — which is the only version of this worth
// having, because the alternative is a viewer that either throws away the whole
// report or silently shows a board that never existed.
//
// **The snapshot is the ground truth and the replay is the reconstruction.**
// They are compared at the end, and a divergence is *reported* rather than
// resolved: the position somebody was actually looking at is the one in the
// snapshot, and a playback that ends somewhere else has to say so rather than
// quietly redefining what the player saw.

import { applyCommand, newGame, type Command, type GameState } from "src/engine/rules/commands";
import { hashState, hashToHex } from "src/engine/rules/hash";

/** A command that would not replay, and why. */
export interface Fault {
  /** Where it sat in the log, so it can be pointed at. */
  index: number;
  /** The command's own `type`, or "unreadable" for something that is not one. */
  type: string;
  /** What the reducer said. */
  reason: string;
}

export interface Replay {
  /**
   * One state per step, starting with the opening board.
   *
   * `frames[0]` is before any command, so `frames[n]` is the board after `n`
   * commands landed — which makes the transport's index the number of moves
   * played and needs no off-by-one anywhere else.
   */
  frames: GameState[];
  /** Commands that would not apply, in log order. */
  faults: Fault[];
  /**
   * Does the replay end where the report says it ended?
   *
   * The one question that decides whether the playback can be trusted. False
   * means the rules have moved under this log — the earlier frames may still be
   * perfectly good, and the *last* one is not what the player saw.
   */
  matches: boolean;
  /** Why there is nothing to play, when there is nothing to play. */
  silent: "no-log" | "empty" | null;
}

/**
 * How many commands a replay will walk before giving up.
 *
 * A guard rather than a limit anybody should reach: this runs in the browser on
 * a dashboard, and a report from a very long game should make the page slow to
 * open rather than unresponsive. Reports past this show the frames they got and
 * say the log was longer.
 */
export const MAX_STEPS = 4_000;

/** Is this plausibly one of our commands? */
function commandish(value: unknown): value is Command {
  return typeof value === "object" && value !== null && typeof (value as Command).type === "string";
}

/**
 * Replay a report's log, skipping whatever will not apply.
 *
 * `reported` is the snapshot the report carried — the board the player was
 * actually looking at. It is used for the comparison at the end and nowhere
 * else: the replay never starts from it, because a snapshot is a destination
 * and the point of this is the journey.
 */
export function replayReport(log: readonly unknown[] | null, reported: GameState | null): Replay {
  const base = newGame();
  const frames: GameState[] = [base];
  const faults: Fault[] = [];

  if (!log) return { frames, faults, matches: false, silent: "no-log" };
  if (log.length === 0) {
    // **A restored run lands a snapshot with an empty log**, which is not a
    // fault and must not read as one: `GameStore.restore` is what Continue
    // does. There is a board and there is no journey to it.
    return { frames, faults, matches: false, silent: "empty" };
  }

  let state = base;
  for (const [index, entry] of log.slice(0, MAX_STEPS).entries()) {
    if (!commandish(entry)) {
      faults.push({ index, type: "unreadable", reason: "not a command" });
      continue;
    }
    try {
      state = applyCommand(state, entry);
      frames.push(state);
    } catch (error) {
      // **Skipped and counted, never fatal.** The command the rules have
      // stopped accepting is usually one of many, and the fifty before it
      // replayed perfectly — throwing the report away over the fifty-first is
      // losing the evidence to protect the viewer from a gap in it.
      faults.push({
        index,
        type: entry.type,
        reason: error instanceof Error ? error.message : String(error)
      });
    }
  }

  const ended = frames[frames.length - 1];
  return {
    frames,
    faults,
    // Compared by hash rather than by eye: two boards that differ in a buffer
    // somewhere look identical on screen, and "the playback is exact" is a
    // claim that has to be checked rather than assumed from the picture.
    matches: reported !== null && hashState(ended) === hashState(reported),
    silent: null
  };
}

/** What the replay did, in one line, for somebody deciding whether to trust it. */
export function replayLine(replay: Replay, logLength: number): string {
  if (replay.silent === "no-log") {
    return "No command log was sent with this report — the board below is the snapshot.";
  }
  if (replay.silent === "empty") {
    return "This game was continued from a save, so it carries no log to replay. The board below is the snapshot.";
  }
  const played = replay.frames.length - 1;
  const parts = [`${played} of ${logLength} commands replayed`];
  if (replay.faults.length > 0) {
    parts.push(`${replay.faults.length} would not apply on this build`);
  }
  parts.push(replay.matches ? "ends exactly where the report does" : "ends somewhere else than the report");
  return `${parts.join(" · ")}.`;
}

/** The state hash, short, for putting a frame beside the report's own. */
export const frameHash = (state: GameState): string => hashToHex(hashState(state));
